import { c as _c } from "react/compiler-runtime";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type * as React from "react";
import { cn } from "@/lib/utils";

// Кнопка веба покупателя по макету «OSMIX ЛК Покупателя» (компонент `button`).
// Своя, а не вариант @osmix/ui/button: общий пакет живёт и в seller-web/admin.
//
// Размеры (высота · радиус · отступы · подпись · иконка · промежуток):
//   xl  56 · r12 · 0/24 · Manrope 16/21 w600 · 20 · 10  — «К оформлению», лендинг
//   lg  48 · r12 · 0/20 · Manrope 16/21 w600 · 20 · 8   — основной размер
//   md  40 · r10 · 0/16 · Manrope 14/18 w600 · 16 · 6   — «Фильтры», «Загрузить»
//   sm  32 · r8  · 0/12 · Manrope 12/16 w600 · 16 · 6   — «В корзину» в строке, «Удалить»
// В Figma у подписи отступ сверху меньше нижнего (9/12) — это компенсация
// базовой линии; в CSS подпись просто центрируется по высоте.
//
// Варианты:
//   dark    #1F1F1F, белый текст — главная кнопка (В корзину, Оформить, Применить)
//   gray    #989AA2 12 %, текст #1F1F1F — вторичная (В чат, Повторить заказ, Показать ещё)
//   ghost   прозрачная, текст #1F1F1F — «Поделиться», «Сменить город», «Удалить» в строке
//   outline прозрачная с обводкой #EAEAEB — «Выбрать размер»
//   accent  #FA5C2E, белый — редкие акцентные: «Оплатить», «Продолжить покупки»
//   danger  #FD453B, белый — подтверждение удаления
//   text    текстовая кнопка 14/18 w400 без фона и высоты — «Сбросить», «В магазин»
// Неактивная (disabled / aria-disabled) у всех: фон #989AA2 12 %, текст #8A8A8B 46 %.
const buttonVariants = cva("inline-flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap font-semibold outline-none transition-colors select-none focus-visible:ring-2 focus-visible:ring-ds-focus focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed aria-disabled:pointer-events-none aria-disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:shrink-0", {
  variants: {
    variant: {
      dark: "bg-ds-primary text-white hover:bg-ds-ink-2",
      gray: "bg-ds-gray text-ds-primary hover:bg-ds-gray-hover",
      ghost: "bg-transparent text-ds-primary hover:bg-ds-gray",
      outline: "border border-ds-line-2 bg-transparent text-ds-primary hover:bg-ds-gray",
      accent: "bg-ds-accent text-white hover:bg-ds-accent/90",
      danger: "bg-ds-danger text-white hover:bg-ds-danger/90",
      text: "h-auto rounded-none bg-transparent p-0 font-normal text-ds-primary hover:text-ds-accent"
    },
    size: {
      xl: "h-14 gap-2.5 rounded-[12px] px-6 text-[16px] leading-[21px] [&_svg:not([class*='size-'])]:size-5",
      lg: "h-12 gap-2 rounded-[12px] px-5 text-[16px] leading-[21px] [&_svg:not([class*='size-'])]:size-5",
      md: "h-10 gap-1.5 rounded-[10px] px-4 text-[14px] leading-[18px] [&_svg:not([class*='size-'])]:size-4",
      sm: "h-8 gap-1.5 rounded-[8px] px-3 text-[12px] leading-[16px] [&_svg:not([class*='size-'])]:size-4",
      // Круглые кнопки-иконки (action-button): 48 — стрелки галереи, 40 —
      // действия идеи (скачать/поделиться), 32 — компактные.
      "icon-lg": "size-12 rounded-full [&_svg:not([class*='size-'])]:size-6",
      "icon-md": "size-10 rounded-full [&_svg:not([class*='size-'])]:size-6",
      "icon-sm": "size-8 rounded-full [&_svg:not([class*='size-'])]:size-4"
    }
  },
  compoundVariants: [
  // Неактивная — серая, как «Сбросить фильтры» в макете (кроме текстовой).
  {
    variant: ["dark", "gray", "ghost", "outline", "accent", "danger"],
    className: "disabled:border-transparent disabled:bg-ds-gray disabled:text-ds-disabled aria-disabled:border-transparent aria-disabled:bg-ds-gray aria-disabled:text-ds-disabled"
  }, {
    variant: "text",
    className: "h-auto px-0 text-[14px] leading-[18px] disabled:text-ds-disabled aria-disabled:text-ds-disabled"
  }],
  defaultVariants: {
    variant: "dark",
    size: "lg"
  }
});
type ButtonProps = React.ComponentProps<"button"> & VariantProps<typeof buttonVariants> & {
  /** Отрисовать как дочерний элемент (Link и т.п.) с классами кнопки. */
  asChild?: boolean;
  /** Растянуть на всю ширину контейнера. */
  block?: boolean;
};
function Button(t0) {
  const $ = _c(20);
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let type;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      size: t2,
      asChild: t3,
      block: t4,
      type,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = type;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    type = $[7];
  }
  const variant = t1 === undefined ? "dark" : t1;
  const size = t2 === undefined ? "lg" : t2;
  const asChild = t3 === undefined ? false : t3;
  const block = t4 === undefined ? false : t4;
  const Comp = asChild ? Slot.Root : "button";
  const t5 = asChild ? type : type ?? "button";
  let t6;
  if ($[8] !== block || $[9] !== className || $[10] !== size || $[11] !== variant) {
    t6 = cn(buttonVariants({
      variant,
      size
    }), block && "w-full", className);
    $[8] = block;
    $[9] = className;
    $[10] = size;
    $[11] = variant;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== Comp || $[14] !== props || $[15] !== size || $[16] !== t5 || $[17] !== t6 || $[18] !== variant) {
    t7 = <Comp data-slot="button" data-variant={variant} data-size={size} type={t5} className={t6} {...props} />;
    $[13] = Comp;
    $[14] = props;
    $[15] = size;
    $[16] = t5;
    $[17] = t6;
    $[18] = variant;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
export { Button, buttonVariants };
export type { ButtonProps };